import React, { useEffect, useState } from 'react';

import {
	useGetRolePermissionsQuery,
	useSaveRolePermissionsMutation,
} from '../api/rolePermissions';
import { useToast } from '../context/ToastContext';

import { Button } from './common';

import type {
	ApproverRole,
	BusinessRole,
	RolePermissions,
} from '../api/rolePermissions';

const COLUMNS: { id: BusinessRole; label: string }[] = [
	{ id: 'sales', label: 'Sales' },
	{ id: 'pricing', label: 'Pricing team' },
	{ id: 'manager', label: 'Manager' },
	{ id: 'admin', label: 'Admin' },
];

// Fixed capabilities of each role (v9 "Permissions by role"); only the rows below are editable.
const FIXED_ROWS: { label: string; cells: [string, string, string, string] }[] =
	[
		{ label: 'See assigned merchants', cells: ['Yes', 'Yes', 'Yes', 'Yes'] },
		{
			label: 'Edit quote inside rep range',
			cells: ['Yes', 'Yes', 'Yes', 'Yes'],
		},
		{
			label: 'Edit quote inside template range',
			cells: ['No', 'Yes', 'Yes', 'Yes'],
		},
		{ label: 'Manage pricing templates', cells: ['No', 'Yes', 'Yes', 'Yes'] },
		{ label: 'Approve exceptions', cells: ['No', 'Rep range', 'Yes', 'Yes'] },
	];

const TRAILING_ROWS: typeof FIXED_ROWS = [
	{ label: 'See analytics', cells: ['No', 'No', 'Yes', 'Yes'] },
	{
		label: 'Manage team and integrations',
		cells: ['No', 'No', 'No', 'Yes'],
	},
];

const APPROVAL_ROWS: {
	key: 'approve_rate_outside_template' | 'approve_margin_below_floor';
	label: string;
}[] = [
	{
		key: 'approve_rate_outside_template',
		label: 'Approves a rate outside the template range',
	},
	{
		key: 'approve_margin_below_floor',
		label: 'Approves a deal margin below the floor',
	},
];

// v9 `table.compact.matrix`: capability in primary-700, Yes in green, No in gray-400.
const TH =
	'bg-gray-50 px-4 py-2.5 text-left text-xs font-medium uppercase tracking-[.05em] text-gray-500 border-b border-gray-200 whitespace-nowrap';
const TD =
	'px-4 py-2.5 text-sm text-gray-900 border-b border-gray-200 whitespace-nowrap align-middle';
const LABEL_TD = `${TD} text-primary-700`;

const FixedRow: React.FC<{ row: (typeof FIXED_ROWS)[number] }> = ({ row }) => (
	<tr>
		<td className={LABEL_TD}>{row.label}</td>
		{row.cells.map((cell, i) => (
			<td key={COLUMNS[i].id} className={TD}>
				<span
					className={
						cell === 'No' ? 'text-gray-400' : 'font-semibold text-green-700'
					}
				>
					{cell}
				</span>
			</td>
		))}
	</tr>
);

// v9 `.switch`: 36x20 track, 16px knob; on = primary.
const Switch: React.FC<{
	checked: boolean;
	disabled: boolean;
	label: string;
	onChange: () => void;
}> = ({ checked, disabled, label, onChange }) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={label}
		disabled={disabled}
		onClick={onChange}
		className="inline-flex items-center cursor-pointer disabled:cursor-not-allowed"
	>
		<i
			className={`relative block h-5 w-9 rounded-full transition-colors ${
				checked ? 'bg-primary-500' : 'bg-gray-300'
			}`}
		>
			<i
				className={`absolute top-0.5 block h-4 w-4 rounded-full bg-white transition-all ${
					checked ? 'left-[18px]' : 'left-0.5'
				}`}
			/>
		</i>
	</button>
);

const RolePermissionsCard: React.FC = () => {
	const { data } = useGetRolePermissionsQuery();
	const [save, { isLoading: saving }] = useSaveRolePermissionsMutation();
	const { showToast } = useToast();
	const [draft, setDraft] = useState<RolePermissions | null>(null);

	useEffect(() => {
		if (data) setDraft(data.permissions);
	}, [data]);

	if (!draft) return null;

	const isAdmin = data?.role === 'admin';

	const toggleProfit = (role: BusinessRole) =>
		setDraft({
			...draft,
			see_iso_profit: draft.see_iso_profit.includes(role)
				? draft.see_iso_profit.filter((r) => r !== role)
				: [...draft.see_iso_profit, role],
		});

	const handleSave = async () => {
		try {
			await save(draft).unwrap();
			showToast('Saved', 'Role permissions updated', 'success');
		} catch {
			showToast('Error', 'Could not save role permissions', 'error');
		}
	};

	return (
		<div className="w-full mt-4 overflow-hidden bg-white border border-gray-200 rounded-lg">
			<div className="flex items-center justify-between gap-4 px-6 pt-6">
				<div>
					<div className="text-lg font-normal text-gray-900">
						Permissions by role
					</div>
					<div className="mt-0.5 text-sm text-gray-500">
						What each role can do, who sees ISO profit, and who approves an
						exception
					</div>
				</div>
				{isAdmin && (
					<Button
						variant="primary"
						onClick={handleSave}
						loading={saving}
						disabled={saving}
					>
						Save
					</Button>
				)}
			</div>
			<div className="overflow-x-auto mt-6">
				<table className="min-w-full border-collapse [&_tbody_tr:last-child_td]:border-b-0">
					<thead>
						<tr>
							<th className={TH}>Capability</th>
							{COLUMNS.map((c) => (
								<th key={c.id} className={TH}>
									{c.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{FIXED_ROWS.slice(0, 3).map((row) => (
							<FixedRow key={row.label} row={row} />
						))}
						<tr>
							<td className={LABEL_TD}>See ISO profit</td>
							{COLUMNS.map((c) => (
								<td key={c.id} className={TD}>
									<Switch
										label={`${c.label} sees ISO profit`}
										checked={draft.see_iso_profit.includes(c.id)}
										disabled={!isAdmin}
										onChange={() => toggleProfit(c.id)}
									/>
								</td>
							))}
						</tr>
						{FIXED_ROWS.slice(3).map((row) => (
							<FixedRow key={row.label} row={row} />
						))}
						{APPROVAL_ROWS.map((row) => (
							<tr key={row.key}>
								<td className={LABEL_TD}>{row.label}</td>
								{COLUMNS.map((c) => (
									<td key={c.id} className={TD}>
										{c.id === 'sales' ? (
											<span className="text-gray-500">—</span>
										) : (
											<input
												type="radio"
												name={row.key}
												aria-label={`${c.label} ${row.label.toLowerCase()}`}
												checked={draft[row.key] === c.id}
												disabled={!isAdmin}
												onChange={() =>
													setDraft({
														...draft,
														[row.key]: c.id as ApproverRole,
													})
												}
												className="h-4 w-4 cursor-pointer accent-primary-500"
											/>
										)}
									</td>
								))}
							</tr>
						))}
						{TRAILING_ROWS.map((row) => (
							<FixedRow key={row.label} row={row} />
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default RolePermissionsCard;
